import {type UIGameManager} from '../UIGameManager.ts'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {MovingSpriteView} from '../../ui_canvas_engine/MovingSpriteView.ts'
import {Sound} from '../../Sound.ts'
import {type TranslationObject} from '@colonist/i18n'
import {Sprites} from '../UIGameImageDictionary.ts'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler.ts'
import {UIGameBeginnerModeGuideBubble} from '../ui_game_beginner_mode/UIGameBeginnerModeGuideBubble.ts'
import {type MechanicRobberState, type NestedReadonly} from '@colonist/game-states'
import {Point, type TileHex} from '@colonist/game-mechanic-validators'
import {robberSkinController} from '../UIGameRobberSkinController.ts'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import SoundType = Sound.SoundType

export class UIGameMapRobberController implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager
    private readonly robberState: NestedReadonly<MechanicRobberState>

    private readonly robberView: MovingSpriteView
    private lastRobberTile: TileHex

    private guideBubbleView?: UIGameBeginnerModeGuideBubble

    constructor(uiGameManager: UIGameManager, robberState: MechanicRobberState) {
        this.uiGameManager = uiGameManager
        this.robberState = robberState

        const tileWithRobber = this.uiGameManager.gameState.mapState.tileState.tiles[this.robberState.locationTileIndex]
        if(tileWithRobber == undefined) return

        this.lastRobberTile = tileWithRobber
        const robberCoords = this.getRobberCoords(this.lastRobberTile)
        const texture = UIGameSpriteHandler.getTextureFromSpritesheet(robberSkinController.getRobberSkin())
        this.robberView = new MovingSpriteView(robberCoords.x, robberCoords.y, this.uiGameManager.mapController.mapView.robberRadius, this.uiGameManager.mapController.mapView.robberRadius, this.uiGameManager.mapController.mapView, texture)
        this.robberView.zIndex = this.uiGameManager.mapController.mapView.layers.robber
        this.robberView.animationController.animationCompleteCallBack = () => {
            this.robberView.zIndex = this.uiGameManager.mapController.mapView.layers.robber
            this.uiGameManager.mapController.mapView.sortChildren()
        }
        const robberTipTitle: TranslationObject = {key: 'strings:game.tips.robber.title'}
        const robberTipBody: TranslationObject[] = [{key: 'strings:game.tips.robber.body.0'}, {key: 'strings:game.tips.robber.body.1'}]
        this.robberView.setTip(robberTipTitle, robberTipBody)

        this.uiGameManager.eventController.registerUIGameMechanic(this)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.MapStateUpdated: this.updateRobber(data); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            default: break
        }
    }

    private updateRobber(animate: boolean) {
        const robberTile = this.uiGameManager.gameState.mapState.tileState.tiles[this.robberState.locationTileIndex]
        if(robberTile == undefined) return
        if(this.lastRobberTile.sameTileHex(robberTile)) return

        this.moveRobber(robberTile, animate)
    }

    private moveRobber(tile: TileHex, animate: boolean) {
        this.robberView.zIndex = this.uiGameManager.mapController.mapView.layers.topLayer
        this.uiGameManager.mapController.mapView.sortChildren()

        const robberCoords = this.getRobberCoords(tile)
        this.robberView.animationController.animationCompleteCallBack = () => {
            if(animate) Sound.playSound(SoundType.RobberPlace)
            this.redrawGuideBubble()
        }
        this.robberView.animationController.animateTo(robberCoords)
        this.lastRobberTile = tile
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.RobberMoved)
    }

    private getRobberCoords(tile: TileHex) {
        const tileCenter = tile.hexFace.toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2)
        const x = tileCenter.x - this.uiGameManager.mapController.mapView.robberRadius * 1.2
        const y = tileCenter.y - this.uiGameManager.mapController.mapView.robberRadius * 0.7
        return new Point(x, y)
    }

    private onBeginnerModeStart() {
        if(this.robberView == undefined) return
        this.guideBubbleView = new UIGameBeginnerModeGuideBubble(this.uiGameManager, this.robberView)
        this.uiGameManager.toolTipController.disableTip(this.robberView)
        this.robberView.texture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_robber_tie)
    }

    private redrawGuideBubble() {
        if(this.guideBubbleView == undefined) return

        const currentBeginnerModeAction = this.guideBubbleView.currentBeginnerModeAction
        this.guideBubbleView.destroy()
        this.guideBubbleView = new UIGameBeginnerModeGuideBubble(this.uiGameManager, this.robberView)
        this.guideBubbleView.currentBeginnerModeAction = currentBeginnerModeAction
    }
}
